import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EnhancedTable from '../Table/EnhancedTable';
import fetchRunFiles from 'store/fetchRunFiles';
import api from 'api';
import createZipFilename from 'pages/WorkflowRunsPage/Files/createZipFilename';
import columns from 'pages/WorkflowRunsPage/Files/columns';
import IRunFile from 'types/IRunFile';
import IPaginatedResponse from 'types/IPaginatedResponse';

interface IProps {
  data: IPaginatedResponse<IRunFile>;
  runId: string;
  projectId: string | undefined;
}

export default function FileTable({ data, runId, projectId }: IProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [downloading, setDownloading] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pageSizeRef = useRef(pageSize);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        const accepted = fetchRunFiles(runId, {
          page: 1,
          pageSize: pageSizeRef.current,
          search: value || undefined,
        });
        if (accepted) {
          setDebouncedSearch(value);
          setPage(1);
        }
      }, 400);
    },
    [runId],
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const accepted = fetchRunFiles(runId, {
        page: newPage,
        pageSize,
        search: debouncedSearch || undefined,
      });
      if (accepted) {
        setPage(newPage);
      }
    },
    [runId, pageSize, debouncedSearch],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const accepted = fetchRunFiles(runId, {
        page: 1,
        pageSize: newPageSize,
        search: debouncedSearch || undefined,
      });
      if (accepted) {
        setPageSize(newPageSize);
        setPage(1);
      }
    },
    [runId, debouncedSearch],
  );

  const handleDownloadZip = useCallback(async () => {
    if (!projectId) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      const qs = params.toString();
      const url = `projects/${projectId}/workflows/${runId}/files/download${qs ? `?${qs}` : ''}`;
      const blob = await api.get(url).blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = createZipFilename(runId) + '.zip';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // error toasting handled globally by api client
    } finally {
      setDownloading(false);
    }
  }, [projectId, runId, debouncedSearch]);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          id="search-workflow-run-files"
          label="Search"
          variant="outlined"
          fullWidth
          value={search}
          onChange={handleSearchChange}
        />
        <Tooltip
          title={
            debouncedSearch ? `Download all files matching "${debouncedSearch}" as ZIP` : 'Download all files as ZIP'
          }
        >
          <span>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadZip}
              disabled={downloading || !projectId}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {downloading ? 'Downloading…' : 'Download ZIP'}
            </Button>
          </span>
        </Tooltip>
      </Box>
      <br />
      <EnhancedTable<IRunFile>
        columns={columns}
        items={data.items}
        serverPagination={{
          page,
          pageSize,
          total: data.total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
      />
    </>
  );
}
